import React, { useState } from 'react';
import { Box, Button, IconButton, Badge, Tooltip } from '@mui/material';
import { User } from '@supabase/supabase-js';
import { AdminPanelSettings, HelpOutline, Mail } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import XERLogo from '../../XERLogo';
import { UserProfile } from '../../../lib/auth-state-machine';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { InboxModal } from './InboxModal';
import { UserMenuDropdown } from './UserMenuDropdown';
import { useTranslation, LanguageSwitcher } from '../../../i18n';
import {
  Header,
  HeaderContainer,
  HeaderContent,
  LeftSection,
  LogoAndTitle,
  MaintenanceTitle,
  UserInfo,
  AuthStatus,
} from '../Dashboard.styles';
import { isAdmin as checkIsAdmin } from '../../../config/roles';
import { useUnreadAnnouncementCountQuery } from './dashboard.queries';

interface DashboardHeaderProps {
  user: User | null;
  profile: UserProfile | null;
  onSignOut: () => Promise<void>;
  isLoggingOut?: boolean;
}

// Inbox Button Component with unread badge
const InboxButton: React.FC<{ user: User | null; onInboxClick: () => void }> = ({
  user,
  onInboxClick,
}) => {
  const { data: unreadCount = 0 } = useUnreadAnnouncementCountQuery(!!user);

  if (!user) return null;

  return (
    <IconButton
      onClick={onInboxClick}
      sx={{
        mr: 2,
        color: BRAND_COLORS.forestGreen.main,
        '&:hover': {
          backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
        },
      }}
    >
      <Badge badgeContent={unreadCount} color="error" max={99}>
        <Mail />
      </Badge>
    </IconButton>
  );
};

// Simplified Admin Tools Button Component
const AdminToolsButton: React.FC<{ profile: UserProfile | null }> = ({ profile }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Don't render if user is not admin
  const isAdmin = checkIsAdmin(profile?.organization?.roles);

  if (!isAdmin) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
      <Button
        variant="outlined"
        size="medium"
        onClick={() => navigate('/admin-tools')}
        startIcon={<AdminPanelSettings />}
        sx={{
          borderColor: BRAND_COLORS.yellow.main,
          color: BRAND_COLORS.yellow.main,
          backgroundColor: BRAND_COLORS.white.main,
          textTransform: 'none',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          '&:hover': {
            borderColor: BRAND_COLORS.yellow.main,
            backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.1),
          },
        }}
      >
        {t('header.adminTools')}
      </Button>
    </Box>
  );
};

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  user,
  profile,
  onSignOut,
  isLoggingOut = false,
}) => {
  const [inboxOpen, setInboxOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleInboxClick = () => {
    setInboxOpen(true);
  };

  const handleInboxClose = () => {
    setInboxOpen(false);
  };

  return (
    <Header component="header">
      <HeaderContainer>
        <HeaderContent>
          <LeftSection>
            {/* Help / User Guide */}
            <Tooltip title={t('guide.headerTooltip')}>
              <IconButton
                onClick={() => navigate('/guide')}
                sx={{
                  color: BRAND_COLORS.forestGreen.main,
                  '&:hover': {
                    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                  },
                }}
              >
                <HelpOutline />
              </IconButton>
            </Tooltip>

            {/* Language Switcher - before inbox */}
            <LanguageSwitcher variant="compact" />

            {/* Inbox Button */}
            <InboxButton user={user} onInboxClick={handleInboxClick} />

            <Button
              variant="outlined"
              size="medium"
              // Same-origin handoff (#617): Analytics SPA reads everything
              // from the JWT in shared localStorage — no URL params needed.
              onClick={() => {
                window.open('/analytics/', '_blank', 'noopener,noreferrer');
              }}
              sx={{
                mr: 4,
                borderColor: BRAND_COLORS.yellow.main,
                color: BRAND_COLORS.yellow.main,
                backgroundColor: BRAND_COLORS.white.main,
                textTransform: 'none',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: BRAND_COLORS.yellow.main,
                  backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.1),
                },
              }}
            >
              {t('header.plotFlightData')}
            </Button>
            <LogoAndTitle>
              <XERLogo width={82} color={BRAND_COLORS.forestGreen.main} />
              <MaintenanceTitle variant="h3">{t('common.appName')}</MaintenanceTitle>
            </LogoAndTitle>
          </LeftSection>
          <UserInfo>
            {user ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Admin Tools Button for Admins */}
                <AdminToolsButton profile={profile} />

                {/* User Menu with Profile Link and Sign Out */}
                <UserMenuDropdown
                  user={user}
                  profile={profile}
                  onSignOut={onSignOut}
                  isLoggingOut={isLoggingOut}
                />
              </Box>
            ) : (
              <AuthStatus>{t('auth.authenticationRequired')}</AuthStatus>
            )}
          </UserInfo>
        </HeaderContent>
      </HeaderContainer>

      {/* Inbox Modal */}
      <InboxModal open={inboxOpen} onClose={handleInboxClose} />
    </Header>
  );
};
